import { useState } from "react";
import { X, CreditCard, Lock, Loader2 } from "lucide-react";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { toast } from "sonner";

interface Plan {
  price: string;
  period: string;
  title: string;
}

interface CheckoutDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  selectedPlan: Plan | null;
}

const CheckoutDrawer = ({ isOpen, onClose, selectedPlan }: CheckoutDrawerProps) => {
  const [cardholderName, setCardholderName] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [cvv, setCvv] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);

  const formatCardNumber = (value: string) => {
    const v = value.replace(/\s+/g, "").replace(/[^0-9]/gi, "");
    const matches = v.match(/\d{4,16}/g);
    const match = (matches && matches[0]) || "";
    const parts = [];

    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }

    if (parts.length) {
      return parts.join(" ");
    } else {
      return value;
    }
  };

  const formatExpiryDate = (value: string) => {
    const v = value.replace(/\s+/g, "").replace(/[^0-9]/gi, "");
    if (v.length >= 2) {
      return v.substring(0, 2) + "/" + v.substring(2, 4);
    }
    return v;
  };

  const handleCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCardNumber(e.target.value);
    if (formatted.replace(/\s/g, "").length <= 16) {
      setCardNumber(formatted);
    }
  };

  const handleExpiryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\//g, "");
    if (value.length <= 4) {
      setExpiryDate(formatExpiryDate(value));
    }
  };

  const handleCvvChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9]/g, "");
    if (value.length <= 4) {
      setCvv(value);
    }
  };

  const handlePayment = async () => {
    if (!cardholderName.trim()) {
      toast.error("Inserisci il nome del titolare");
      return;
    }
    if (cardNumber.replace(/\s/g, "").length < 16) {
      toast.error("Inserisci un numero di carta valido");
      return;
    }
    if (expiryDate.length < 5) {
      toast.error("Inserisci una data di scadenza valida");
      return;
    }
    if (cvv.length < 3) {
      toast.error("Inserisci un CVV valido");
      return;
    }

    setIsProcessing(true);
    
    // Simulate processing delay
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    setIsProcessing(false);
    toast.info("Funzionalità in arrivo!", {
      description: "Il sistema di pagamento sarà disponibile presto.",
    });
    
    // Reset form
    setCardholderName("");
    setCardNumber("");
    setExpiryDate("");
    setCvv("");
    onClose();
  };

  const handleClose = () => {
    if (!isProcessing) {
      setCardholderName("");
      setCardNumber("");
      setExpiryDate("");
      setCvv("");
      onClose();
    }
  };

  return (
    <Drawer open={isOpen} onOpenChange={handleClose}>
      <DrawerContent className="h-[85vh] bg-background/95 backdrop-blur-xl border-t border-border/50">
        <DrawerHeader className="relative border-b border-border/30 pb-4">
          <button
            onClick={handleClose}
            disabled={isProcessing}
            className="absolute left-4 top-1/2 -translate-y-1/2 p-2 rounded-full hover:bg-muted/50 transition-all duration-300 hover:scale-110 active:scale-90 disabled:opacity-50"
          >
            <X className="w-5 h-5 text-foreground" />
          </button>
          <DrawerTitle className="text-xl font-bold text-center">Checkout</DrawerTitle>
        </DrawerHeader>

        <div className="flex flex-col p-6 gap-6 overflow-y-auto">
          {/* Selected Plan Summary */}
          {selectedPlan && (
            <div className="p-4 rounded-2xl bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Piano selezionato</p>
                  <p className="text-lg font-bold text-foreground">{selectedPlan.title}</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-primary">{selectedPlan.price}</p>
                  <p className="text-xs text-muted-foreground">/ {selectedPlan.period}</p>
                </div>
              </div>
            </div>
          )}

          {/* Payment Form */}
          <div className="space-y-5">
            <div className="flex items-center gap-2 text-muted-foreground">
              <CreditCard className="w-5 h-5" />
              <span className="text-sm font-medium">Dati della carta</span>
            </div>

            {/* Cardholder Name */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">
                Nome e Cognome del titolare
              </label>
              <input
                type="text"
                value={cardholderName}
                onChange={(e) => setCardholderName(e.target.value)}
                placeholder="Mario Rossi"
                disabled={isProcessing}
                className="w-full px-4 py-3.5 rounded-xl bg-muted/50 border border-border/50 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all duration-300 disabled:opacity-50"
              />
            </div>

            {/* Card Number */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">
                Numero della Carta
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={cardNumber}
                  onChange={handleCardNumberChange}
                  placeholder="1234 5678 9012 3456"
                  disabled={isProcessing}
                  className="w-full px-4 py-3.5 pr-12 rounded-xl bg-muted/50 border border-border/50 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all duration-300 disabled:opacity-50 font-mono tracking-wider"
                />
                <CreditCard className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              </div>
            </div>

            {/* Expiry and CVV */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">
                  Scadenza (MM/AA)
                </label>
                <input
                  type="text"
                  value={expiryDate}
                  onChange={handleExpiryChange}
                  placeholder="MM/AA"
                  disabled={isProcessing}
                  className="w-full px-4 py-3.5 rounded-xl bg-muted/50 border border-border/50 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all duration-300 disabled:opacity-50 font-mono text-center"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">
                  CVV
                </label>
                <input
                  type="password"
                  value={cvv}
                  onChange={handleCvvChange}
                  placeholder="•••"
                  disabled={isProcessing}
                  className="w-full px-4 py-3.5 rounded-xl bg-muted/50 border border-border/50 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all duration-300 disabled:opacity-50 font-mono text-center"
                />
              </div>
            </div>

            {/* Security Notice */}
            <div className="flex items-center gap-2 p-3 rounded-xl bg-muted/30 border border-border/30">
              <Lock className="w-4 h-4 text-primary" />
              <p className="text-xs text-muted-foreground">
                I tuoi dati sono protetti con crittografia SSL
              </p>
            </div>
          </div>

          {/* Pay Button */}
          <button
            onClick={handlePayment}
            disabled={isProcessing}
            className="w-full py-4 px-6 bg-primary text-primary-foreground font-semibold rounded-xl transition-all duration-300 hover:bg-primary/90 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-70 disabled:hover:scale-100 flex items-center justify-center gap-2 shadow-lg shadow-primary/20"
          >
            {isProcessing ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Elaborazione...</span>
              </>
            ) : (
              <>
                <span>Paga ora</span>
                {selectedPlan && <span className="opacity-80">• {selectedPlan.price}</span>}
              </>
            )}
          </button>
        </div>
      </DrawerContent>
    </Drawer>
  );
};

export default CheckoutDrawer;